import Plot from 'features/plotly'
import {
  getChartImageOptions,
  ScatterPlotEmptyStateContainer,
  ScatterPlotEmptyStateDescription,
  ScatterPlotEmptyStateTitle,
} from 'features/timeSeries'
import { InterpolatedValueTooltip } from 'features/timeSeries/components/InterpolatedValueTooltip'
import { ScatterplotSidebarView } from 'features/timeSeries/components/ScatterplotSidebar'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { Layout, PlotData, PlotHoverEvent, PlotRelayoutEvent } from 'plotly.js'
import React from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

const StyledPlot = styled(Plot)`
  width: 100%;
  height: 100%;
`

const ChartArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-left: 1.5rem;
  background: ${CHART_COLORS.BACKGROUND};
  overflow: visible;
`

const LoadingSpinner = styled.div<{
  $isVisible: boolean
  $shouldAnimate: boolean
}>`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid ${({ theme }) => theme.forecast.background3};
  border-top: 2px solid ${({ theme }) => theme.forecast.icon1};
  border-radius: 50%;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  animation: ${({ $shouldAnimate }) =>
    $shouldAnimate ? 'spin 0.33s linear infinite' : 'none'};
  pointer-events: none;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const EmptyStateContainer = styled(ScatterPlotEmptyStateContainer)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`

interface TimeSeriesScatterplotChartProps {
  handleMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void
  internalRef: React.MutableRefObject<HTMLDivElement | null>
  cursorPosition: {
    x: number
    y: number
    clientX: number
    clientY: number
  } | null
  tableData: SensorScatterplotData[]
  sensorVisibilityConfig: Record<string, boolean>
  shouldShowSpinner: boolean
  shouldAnimateSpinner: boolean
  isResizing: boolean
  sidebarView?: ScatterplotSidebarView
  hasDataPoints: boolean
  plotlyData: Partial<PlotData>[]
  plotlyLayout: Partial<Layout>
  handleHover: (event: PlotHoverEvent) => void
  handleUnhover: () => void
  handleRelayout: (event: PlotRelayoutEvent) => void
  isLoading: boolean
  showEmptyState: boolean
  selectedSensors: Array<{ id: string }>
  derivedValues: Array<{ id: string }>
}

export const TimeSeriesScatterplotChart = React.forwardRef<
  HTMLDivElement,
  TimeSeriesScatterplotChartProps
>(
  (
    {
      handleMouseMove,
      internalRef,
      cursorPosition,
      tableData,
      sensorVisibilityConfig,
      shouldShowSpinner,
      shouldAnimateSpinner,
      isResizing,
      sidebarView,
      hasDataPoints,
      plotlyData,
      plotlyLayout,
      handleHover,
      handleUnhover,
      handleRelayout,
      isLoading,
      showEmptyState,
      selectedSensors,
      derivedValues,
    },
    ref
  ) => {
    return (
      <ChartArea
        onMouseMove={handleMouseMove}
        ref={(node) => {
          internalRef.current = node
          if (ref) {
            if (typeof ref === 'function') {
              ref(node)
            } else {
              ref.current = node
            }
          }
        }}>
        <InterpolatedValueTooltip
          cursorPosition={cursorPosition}
          tableData={tableData}
          sensorVisibilityConfig={sensorVisibilityConfig}
        />
        <LoadingSpinner
          $isVisible={shouldShowSpinner}
          $shouldAnimate={shouldAnimateSpinner}
        />
        {isResizing ? null : (
          <StyledPlot
            key={`chart-${sidebarView}`}
            data={hasDataPoints ? plotlyData : []}
            layout={plotlyLayout}
            config={{
              displayModeBar: true,
              displaylogo: false,
              modeBarButtonsToRemove: ['lasso2d', 'select2d'],
              toImageButtonOptions: getChartImageOptions(),
              responsive: true,
            }}
            useResizeHandler={true}
            onHover={handleHover}
            onUnhover={handleUnhover}
            onRelayout={handleRelayout}
          />
        )}
        {!hasDataPoints &&
          (() => {
            const hasConfiguredData =
              selectedSensors.length > 0 || derivedValues.length > 0

            if (isLoading || !showEmptyState) {
              return (
                <EmptyStateContainer>
                  <ScatterPlotEmptyStateTitle>
                    Loading
                  </ScatterPlotEmptyStateTitle>
                </EmptyStateContainer>
              )
            } else if (hasConfiguredData) {
              return (
                <EmptyStateContainer>
                  <ScatterPlotEmptyStateTitle>
                    No data
                  </ScatterPlotEmptyStateTitle>
                  <ScatterPlotEmptyStateDescription>
                    No data available in the selected time range.
                  </ScatterPlotEmptyStateDescription>
                </EmptyStateContainer>
              )
            } else {
              return (
                <EmptyStateContainer>
                  <ScatterPlotEmptyStateTitle>
                    No data
                  </ScatterPlotEmptyStateTitle>
                  <ScatterPlotEmptyStateDescription>
                    Use the Configure button to add data to the plot.
                  </ScatterPlotEmptyStateDescription>
                </EmptyStateContainer>
              )
            }
          })()}
      </ChartArea>
    )
  }
)

TimeSeriesScatterplotChart.displayName = 'TimeSeriesScatterplotChart'
